/**
 * O pedido do cliente na abertura (E144.F3, GUS-135, ADR-0098 §2.6; E165.F1.T03, HTML 6.8): o prazo
 * processual e a urgência, recolhidos dentro do card de identificação, e as observações, recolhidas
 * num card próprio depois dos arquivos (GUS-202). Sem as explicações embaixo dos campos: o prazo é
 * a data comunicada à equipe, e o pedido de urgência leva o motivo, que a equipe avalia.
 *
 * E163.F2 (GUS-209 A67-07): a hora saiu da tela. A hora já gravada no processo continua no pedido e
 * volta ao servidor como estava; só some junto com a data, que ela exige.
 *
 * Recolhido é `hidden`, não desmontado: o que foi digitado continua no pedido ao fechar o bloco.
 * O bloco com erro abre sozinho (erro escondido é erro que ninguém corrige).
 *
 * E165.F1.C01 (GUS-221 F03): os dois blocos começam recolhidos sempre, também com valor — ao voltar
 * do Conferir e ao recarregar, o bloco não se abre sem a pessoa pedir; o valor segue no pedido.
 *
 * E165.F1.C02 (GUS-221 F03, reteste de 23/10 à noite): a data que chega ao campo sem passar pelo
 * `onChange` do React (posta por script, como a automação do navegador faz num campo de data) ia só
 * para a tela; o render seguinte, ao recolher, devolvia o campo ao pedido, vazio. O campo de data
 * também lê o valor no `input` e ao sair dele, e o leva ao pedido quando difere.
 */
import { useState, type SyntheticEvent } from 'react';

import { CasoField } from '@/components/NovoCaso/NovoCasoFormField';
import { Input, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ErroDoPedido, PedidoDaAbertura } from '@/lib/abertura';

import { Recolhivel } from './PecasDaAbertura';

const t = ptBR.abertura.pedido;

export interface PedidoDoClienteProps {
  valor: PedidoDaAbertura;
  onChange: (valor: PedidoDaAbertura) => void;
  erro: ErroDoPedido | null;
  disabled?: boolean;
}

/** "Prazo processual e urgência", dentro do card de identificação. */
export function PrazoEUrgencia({
  valor,
  onChange,
  erro,
  disabled = false,
}: PedidoDoClienteProps): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const mudarPrazo = (e: SyntheticEvent<HTMLInputElement>): void => {
    const prazo = e.currentTarget.value;
    if (prazo === valor.prazo) return;
    onChange({ ...valor, prazo, hora: prazo === '' ? '' : valor.hora });
  };
  return (
    <div className="mt-sc-4">
      <Recolhivel
        titulo={t.prazoTitulo}
        aberto={aberto || erro !== null}
        onAberto={setAberto}
        testId="abertura-pedido-prazo"
      >
        <CasoField
          id="abertura-prazo-processual"
          label={t.prazo}
          {...(erro === 'hora_sem_prazo' ? { error: t.horaSemPrazo } : {})}
        >
          <Input
            id="abertura-prazo-processual"
            data-testid="abertura-prazo-processual"
            type="date"
            value={valor.prazo}
            disabled={disabled}
            onChange={mudarPrazo}
            onInput={mudarPrazo}
            onBlur={mudarPrazo}
            className="max-w-xs font-mono"
          />
        </CasoField>
        <label className="flex items-center gap-sc-2 text-[14px] text-fg">
          <input
            type="checkbox"
            data-testid="abertura-urgencia"
            checked={valor.urgente}
            disabled={disabled}
            onChange={(e) => onChange({ ...valor, urgente: e.target.checked })}
            className="h-4 w-4"
          />
          {t.urgencia}
        </label>
        {valor.urgente ? (
          <CasoField
            id="abertura-motivo-urgencia"
            label={t.motivo}
            required
            {...(erro === 'urgencia_sem_motivo' ? { error: t.motivoObrigatorio } : {})}
          >
            <Textarea
              id="abertura-motivo-urgencia"
              data-testid="abertura-motivo-urgencia"
              value={valor.motivo}
              maxLength={500}
              disabled={disabled}
              onChange={(e) => onChange({ ...valor, motivo: e.target.value })}
            />
          </CasoField>
        ) : null}
      </Recolhivel>
    </div>
  );
}

/** "Observações", num card próprio depois dos arquivos, recolhido. */
export function ObservacoesDoPedido({
  valor,
  onChange,
  disabled = false,
}: Omit<PedidoDoClienteProps, 'erro'>): JSX.Element {
  const [aberta, setAberta] = useState(false);
  return (
    <section
      className="border border-border bg-surface p-[22px]"
      data-testid="abertura-observacoes"
    >
      <Recolhivel
        titulo={t.observacoesTitulo}
        aberto={aberta}
        onAberto={setAberta}
        testId="abertura-pedido-observacoes"
      >
        <CasoField id="abertura-observacao" label={t.observacao}>
          <Textarea
            id="abertura-observacao"
            data-testid="abertura-observacao"
            value={valor.observacao}
            maxLength={2000}
            disabled={disabled}
            onChange={(e) => onChange({ ...valor, observacao: e.target.value })}
          />
        </CasoField>
      </Recolhivel>
    </section>
  );
}
